import { ArrowRight } from 'lucide-react';
import { StatusBadge } from '@/components/common/StatusBadge';
import { cn } from '@/lib/utils';
import { TABLE_HEADER_CLASS } from '@/styles/dataTableBg';
import { STATUS_SOLID_CLASS, type StatusColorTone } from '@/styles/statusColors';
import { INSTITUTE_VALUE_FIELDS } from '../constants/instituteSubmission.constants';
import type { InstituteValues } from '../types/instituteSubmission.types';
import { toInstituteDisplayValue } from '../utils/instituteValues';

const CHANGE_KINDS = ['updated', 'added', 'cleared'] as const;

type ChangeKind = (typeof CHANGE_KINDS)[number];

const CHANGE_KIND_DISPLAY: Record<ChangeKind, { label: string; tone: StatusColorTone }> = {
  updated: { label: 'Updated', tone: 'info' },
  added: { label: 'Added', tone: 'success' },
  cleared: { label: 'Cleared', tone: 'danger' },
};

const CELL_CLASS = 'px-3 py-2.5 align-top';

function getChangeKind(
  currentValue: string | null,
  proposedValue: string | null,
): ChangeKind | null {
  if (currentValue === proposedValue) return null;
  if (currentValue === null) return 'added';
  if (proposedValue === null) return 'cleared';
  return 'updated';
}

function MissingValue({ children }: { children: string }) {
  return <span className="text-muted-foreground">{children}</span>;
}

interface ChangeKindTagProps {
  kind: ChangeKind;
  count?: number;
}

function ChangeKindTag({ kind, count }: ChangeKindTagProps) {
  const { label, tone } = CHANGE_KIND_DISPLAY[kind];
  return (
    <StatusBadge tone={tone}>
      <span className={cn('size-1.5 rounded-full', STATUS_SOLID_CLASS[tone])} aria-hidden />
      {count !== undefined && <span className="tabular-nums">{count}</span>}
      {label}
    </StatusBadge>
  );
}

interface InstituteChangesSectionProps {
  code: string | null;
  current: InstituteValues;
  proposed: InstituteValues;
}

export function InstituteChangesSection({ code, current, proposed }: InstituteChangesSectionProps) {
  const fieldRows = INSTITUTE_VALUE_FIELDS.map(({ key, label }) => {
    const currentValue = toInstituteDisplayValue(current, key);
    const proposedValue = toInstituteDisplayValue(proposed, key);
    return {
      key,
      label,
      currentValue,
      proposedValue,
      changeKind: getChangeKind(currentValue, proposedValue),
    };
  });
  const changedCount = fieldRows.filter((fieldRow) => fieldRow.changeKind).length;
  const kindCounts = CHANGE_KINDS.map((kind) => ({
    kind,
    count: fieldRows.filter((fieldRow) => fieldRow.changeKind === kind).length,
  })).filter(({ count }) => count > 0);
  const codeRow = {
    key: 'code',
    label: 'Code',
    currentValue: code,
    proposedValue: code,
    changeKind: null as ChangeKind | null,
  };
  const tableRows = [codeRow, ...fieldRows];

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-col">
          <h2 className="text-sm font-semibold">Submitted details</h2>
          <p className="text-xs text-muted-foreground">
            {changedCount === 0
              ? 'No changes - the ZM visited and confirmed the details already on file.'
              : `${changedCount} of ${fieldRows.length} fields differ from what is on file`}
          </p>
        </div>
        {kindCounts.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {kindCounts.map(({ kind, count }) => (
              <ChangeKindTag key={kind} kind={kind} count={count} />
            ))}
          </div>
        )}
      </div>

      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full min-w-xl table-fixed text-sm">
          <colgroup>
            <col className="w-40" />
            <col />
            <col className="w-7" />
            <col />
            <col className="w-24" />
          </colgroup>
          <thead className={cn(TABLE_HEADER_CLASS, 'text-xs')}>
            <tr>
              <th scope="col" className="px-3 py-2 text-left font-medium">
                Field
              </th>
              <th scope="col" className="px-3 py-2 text-left font-medium">
                On file
              </th>
              <td aria-hidden />
              <th scope="col" className="px-3 py-2 text-left font-medium">
                Submitted
              </th>
              <th scope="col" className="px-3 py-2">
                <span className="sr-only">Change</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {tableRows.map((fieldRow) => (
              <tr
                key={fieldRow.key}
                className={cn(
                  'border-b last:border-b-0',
                  !fieldRow.changeKind && 'bg-muted/20 text-muted-foreground',
                )}
              >
                <th
                  scope="row"
                  className={cn(
                    CELL_CLASS,
                    'border-l-2 text-left text-xs leading-5 whitespace-nowrap',
                    fieldRow.changeKind
                      ? 'border-l-primary font-semibold text-foreground'
                      : 'border-l-transparent font-medium',
                  )}
                >
                  {fieldRow.label}
                </th>

                {fieldRow.changeKind ? (
                  <>
                    <td className={cn(CELL_CLASS, 'text-muted-foreground wrap-break-word')}>
                      {fieldRow.currentValue === null ? (
                        <MissingValue>Not on file</MissingValue>
                      ) : (
                        <del className="decoration-danger-foreground/60">
                          {fieldRow.currentValue}
                        </del>
                      )}
                    </td>
                    <td className="py-2.5 align-top">
                      <ArrowRight className="mt-0.5 size-4 text-muted-foreground/70" aria-hidden />
                    </td>
                    <td className={cn(CELL_CLASS, 'font-medium text-foreground wrap-break-word')}>
                      {fieldRow.proposedValue ?? <MissingValue>Left blank</MissingValue>}
                    </td>
                    <td className={cn(CELL_CLASS, 'text-right')}>
                      <ChangeKindTag kind={fieldRow.changeKind} />
                    </td>
                  </>
                ) : (
                  <>
                    <td colSpan={3} className={cn(CELL_CLASS, 'wrap-break-word')}>
                      {fieldRow.currentValue ?? <MissingValue>Not set</MissingValue>}
                    </td>
                    <td className={cn(CELL_CLASS, 'text-right text-2xs')}>No change</td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
